import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import {
    FORM_DIRECTIVES,
    FORM_PROVIDERS,
    FormControl,
    FormGroup,
    REACTIVE_FORM_DIRECTIVES,
    Validators
} from "@angular/forms";
import { AlertComponent } from "ng2-bootstrap/ng2-bootstrap";
import { Vehicle } from "../domain/vehicle";
import {
    DATE_VALID_CHARACTERS,
    DATE_VALIDATOR,
    DEFAULT_ERROR_MESSAGES,
    INTEGER_VALIDATOR
} from "../validators/validators";
import { LoginService } from "../service/login.service";
import { KmHistory } from "../domain/kmHistory";
import { KmHistoryService } from "../service/kmHistory.service";

@Component( {
    selector: 'kmHistory-editor',
    template: require( './kmHistory.editor.component.html' ),
    styles: [require( './kmHistory.editor.component.css' )],
    directives: [
        FORM_DIRECTIVES,
        REACTIVE_FORM_DIRECTIVES,
        AlertComponent
    ],
    providers: [FORM_PROVIDERS],
} )
export class KmHistoryEditorComponent implements OnInit, OnDestroy, AfterViewInit {

    @Input() vehicle: Vehicle;
    @Output() close = new EventEmitter();

    private sub: any;

    public errorMessages: any;
    public form: FormGroup;

    private vehicleId: string;

    public errorMessage: string;
    public successMessage: string;
    public warningMessage: string;

    public newKmHistory: KmHistory;

    public loading: boolean;

    public canEdit: boolean;

    private todayStr: string;

    constructor( private router: Router,
        private kmHistoryService: KmHistoryService,
        private route: ActivatedRoute,
        private loginService: LoginService ) {
        this.init();
    }

    private init(): void {
        this.errorMessages = DEFAULT_ERROR_MESSAGES;

        this.newKmHistory = new KmHistory();

        this.loading = false;

        this.canEdit = false;

        let dateStrParts = new Date();
        this.todayStr = this.padLeft( dateStrParts.getDate().toString(), '0', 2 ) + '/' +
            this.padLeft(( dateStrParts.getMonth() + 1 ).toString(), '0', 2 ) + '/' + dateStrParts.getFullYear();

        this.newKmHistory.loadDate = this.todayStr;

        this.vehicle = new Vehicle();
    }

    private padLeft( text: string, padChar: string, size: number ): string {
        return ( String( padChar ).repeat( size ) + text ).substr(( size * -1 ), size );
    }

    private createFormGroup(): void {
        this.form = new FormGroup( {
            type: new FormControl( this.vehicle.type.description ),
            patent: new FormControl( this.vehicle.patent ),
            brand: new FormControl( this.vehicle.brand.name ),
            model: new FormControl( this.vehicle.model ),
            year: new FormControl( this.vehicle.year ),
            engineNumber: new FormControl( this.vehicle.engineNumber ),
            newKmHistoryDate: new FormControl( this.newKmHistory.loadDate, [Validators.required, DATE_VALID_CHARACTERS, DATE_VALIDATOR] ),
            newKmHistoryKm: new FormControl( this.newKmHistory.km, [Validators.required, INTEGER_VALIDATOR] ),
            newKmHistoryService: new FormControl( this.newKmHistory.service ),     
            newKmHistoryComments: new FormControl( this.newKmHistory.comments )
        } );
    }
    
    
    public ngOnInit() {
        let userLogged = this.loginService.getUserLogged();
        this.canEdit = userLogged.isActionAllowed( 'KM_HISTORY', 'EDIT', 'KM_HISTORY' );

        this.createFormGroup();
        
        this.newKmHistory.service = '';
        
        this.sub = this.route.params.subscribe( params => {
            this.vehicleId = params['vehicleId'];
            this.getData();
        } );
    }

    public ngAfterViewInit() {
    }

    private getData(): void {
        this.loading = true;
        this.errorMessage = null;

        this.kmHistoryService.getVehicle( this.vehicleId ).subscribe(
            ( vehicle ) => {
                if ( vehicle && vehicle.id ) {
                    this.vehicle = vehicle;
                }
            },
            () => {
                this.bindError( 'Ocurrió un error al obtener el vehículo.' );
            } );
        this.loading = false;
    }

    public ngOnDestroy() {
        this.sub.unsubscribe();
    }

    private clearMessages(): void {
        this.successMessage = null;
        this.errorMessage = null;
        this.warningMessage = null;
    }

    public save(): void {
        this.clearMessages();
        this.loading = true;

        this.kmHistoryService.save( this.vehicle ).subscribe(
            ( saved: Vehicle ) => {
                this.loading = false;
                this.vehicle = saved;
                this.successMessage = 'La carga de kilómetros se guardó con éxito';                

                if ( this.checkPreSaveKmHistories() ) {
                    this.bindWarning( 'Existen Históricos de kilómetros sin guardar.' );
                }
            },
            () => {
                this.bindError( 'Ocurrió un error al guardar la información.' );
            } );
    }

    public goBack( event: any ): void {
        event.preventDefault();
        this.close.emit( this.vehicle );
        window.history.back();
    }

    private addNewKmHistoryToVehicle(): void {
        this.vehicle.kmHistories.push(( JSON.parse( JSON.stringify( this.newKmHistory ) ) ) );
        this.newKmHistory = new KmHistory();
        this.newKmHistory.loadDate = this.todayStr;
        this.newKmHistory.service = '';        
    }

    public addNewKmHistory( event: any ): void {
        event.preventDefault();
        this.addNewKmHistoryToVehicle();

        this.clearMessages();
        this.bindWarning( 'Existen Históricos de kilómetros sin guardar.' );
    }

    public deleteKmHistory( event: any, index: number, id: string ): void {
        event.preventDefault();

        this.clearMessages();
        this.loading = true;

        if ( id != undefined && id != null ) {
            this.kmHistoryService.delete( id ).subscribe(
                () => {
                    this.loading = false;
                    this.successMessage = 'La carga de kilómetros se eliminó con éxito';

                    this.loading = false;
                    this.vehicle.kmHistories.splice( index, 1 );

                    if ( this.checkPreSaveKmHistories() ) {
                        this.bindWarning( 'Existen Históricos de kilómetros sin guardar.' );
                    }
                },
                () => {
                    this.bindError( 'Ocurrió un error al eliminar el histórico.' );
                } );
        } else {
            this.loading = false;
            this.vehicle.kmHistories.splice( index, 1 );

            if ( this.checkPreSaveKmHistories() ) {
                this.bindWarning( 'Existen Históricos de kilómetros sin guardar.' );
            }
        }
    }

    private bindError( messageError: string ): void {
        this.loading = false;
        if ( this.errorMessage == null ) {
            this.errorMessage = messageError;
        } else {
            this.errorMessage = this.errorMessage.toString() + ' ' + messageError;
        }
    }

    private bindWarning( messageWarning: string ): void {
        this.loading = false;
        if ( this.warningMessage == null ) {
            this.warningMessage = messageWarning;
        } else {
            this.warningMessage = this.warningMessage.toString() + ' ' + messageWarning;
        }
    }

    private checkPreSaveKmHistories(): boolean {
        let hasPreSaveKmHistories = false;
        this.vehicle.kmHistories.forEach( kmHistory => {
            if ( kmHistory.id === undefined || kmHistory.id === null ) {
                hasPreSaveKmHistories = true;
            }
        } );
        return hasPreSaveKmHistories;
    }
}


/** WEBPACK FOOTER **
 ** ./src/app/kmHistory/kmHistory.editor.component.ts
 **/